import { useCallback, useEffect, useMemo, useState } from "react";
import { useUserSession, useFinancialPreferences, useFinancialVisibility } from "shared/contexts/UserContext";
import { useCurrencyConversion } from "shared/contexts/CurrencyConversionContext";
import { useDismissedNotifications } from "shared/mobile/hooks/useDismissedNotifications";
import { useIsMounted } from "shared/hooks/useIsMounted";
import { useTranslation } from "shared/hooks/useTranslation";
import {
    calcUserBalanceByCurrency,
    formatCurrencyAmountOrMasked,
    languageToLocale,
    normalizeTransactionParticipants,
} from "shared/money/balances";
import { calcUserDatedBalanceContributions } from "shared/money/currencyConversion";
import { buildGroupGuideItems } from "lib/guideBuilders";
import {
    getHomeGroupStatusTone,
    lastActivityLabel,
    memberPreviewFor,
} from "features/groups/lib/homeGroupsDisplay";
import type { HomeGroupCardModel } from "features/groups/lib/homeGroupsDisplay";
import { activePeopleFor, dedupePeopleById, participantDisplayName, resolveCurrentParticipantId } from "features/groups/lib/groupPeople";
import { responseCache } from "shared/money/responseCache";
import { getGroupsFromUser } from "features/groups/api/Group";
import type { GroupDTO, GroupSummary } from "types/Group";
import type { GroupTransaction } from "@/types/GroupTransaction";
import { apiGetSWR } from "utils/apiClient";

function homeGroupsEndpoint(username: string): string {
    return `/groups/${username}`;
}

export interface UseHomeScreenParams {
    username: string | undefined;
}

export function useHomeScreen({ username }: UseHomeScreenParams) {
    const { profile } = useUserSession();
    const { userSettings } = useFinancialPreferences();
    const { balancesVisible } = useFinancialVisibility();
    const { convertDatedTotal, convertBalances } = useCurrencyConversion();
    const { t, currentLanguage, getPathWithLanguage } = useTranslation();
    const locale = languageToLocale(currentLanguage);

    const [groups, setGroups] = useState<GroupSummary[]>(() => {
        if (!username) return [];
        const c = responseCache.get<unknown>(homeGroupsEndpoint(username));
        if (!Array.isArray(c)) return [];
        return (c as GroupDTO[]).map((g) => ({
            _id: g._id,
            name: g.name ?? "",
            people: dedupePeopleById(g.people ?? []),
            inviteToken: g.inviteToken,
            transactions: (g.transactions as GroupTransaction[]) ?? [],
            simplifyDebt: g.simplifyDebt,
        }));
    });
    const [isLoading, setIsLoading] = useState<boolean>(() => {
        if (!username) return false;
        return responseCache.get<unknown>(homeGroupsEndpoint(username)) === null;
    });
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [searchOpen, setSearchOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");

    const notificationStorageKey = `group-notifications:${profile.userId ?? profile.username}`;
    const { dismissNotification, filterDismissedNotifications } = useDismissedNotifications(
        notificationStorageKey,
        profile.token,
    );

    const isMounted = useIsMounted();

    const loadGroups = useCallback(async () => {
        if (!username) {
            setIsLoading(false);
            setGroups([]);
            return;
        }

        try {
            setIsLoading(true);
            setErrorMessage(null);
            const userGroups = await getGroupsFromUser(username);
            if (isMounted()) {
                const normalized: GroupSummary[] = (userGroups as GroupDTO[] ?? []).map((g) => ({
                    _id: g._id,
                    name: g.name ?? "",
                    people: dedupePeopleById(g.people ?? []),
                    inviteToken: g.inviteToken,
                    transactions: (g.transactions as GroupTransaction[]) ?? [],
                    simplifyDebt: g.simplifyDebt,
                }));
                setGroups(normalized);
            }
        } catch {
            if (isMounted()) {
                setErrorMessage(t("homePage.error"));
                setGroups([]);
            }
        } finally {
            if (isMounted()) {
                setIsLoading(false);
            }
        }
    }, [username, isMounted, t]);

    useEffect(() => {
        if (!username) {
            setGroups([]);
            setIsLoading(false);
            return;
        }
        setErrorMessage(null);
        const endpoint = homeGroupsEndpoint(username);
        let cancelled = false;

        const applyUserGroups = (userGroups: GroupDTO[]): void => {
            const normalized: GroupSummary[] = (userGroups as GroupDTO[] ?? []).map((g) => ({
                _id: g._id,
                name: g.name ?? "",
                people: dedupePeopleById(g.people ?? []),
                inviteToken: g.inviteToken,
                transactions: (g.transactions as GroupTransaction[]) ?? [],
                simplifyDebt: g.simplifyDebt,
            }));
            if (isMounted()) setGroups(normalized);
        };

        const { stale, done, cancel } = apiGetSWR<GroupDTO[]>(endpoint, (userGroups) => {
            applyUserGroups(userGroups ?? []);
        }, undefined, { isCurrent: () => !cancelled });

        if (stale !== null && Array.isArray(stale)) {
            applyUserGroups(stale);
            setIsLoading(false);
        } else {
            setIsLoading(true);
        }

        void done
            .catch(() => {
                if (isMounted() && !cancelled) {
                    setErrorMessage(t("homePage.error"));
                    setGroups([]);
                }
            })
            .finally(() => {
                if (isMounted() && !cancelled) setIsLoading(false);
            });

        return () => {
            cancelled = true;
            cancel();
        };
    }, [username, isMounted, t]);

    const filteredGroups = useMemo(() => {
        const query = searchQuery.trim().toLowerCase();
        if (!query) return groups;
        return groups.filter((group) => {
            const people = activePeopleFor(group.people ?? []);
            return (
                group.name.toLowerCase().includes(query) ||
                people.some((person) => participantDisplayName(person).toLowerCase().includes(query))
            );
        });
    }, [groups, searchQuery]);

    const rawGroupNotifications = useMemo(
        () => buildGroupGuideItems({
            groups,
            username: profile.username,
            userId: profile.userId,
            balancesVisible,
            locale,
            language: currentLanguage,
            max: 12,
        }),
        [balancesVisible, groups, locale, profile.userId, profile.username, currentLanguage],
    );

    const groupNotifications = useMemo(
        () => filterDismissedNotifications(rawGroupNotifications).slice(0, 3),
        [filterDismissedNotifications, rawGroupNotifications],
    );

    const buildCardModel = useCallback((group: GroupSummary): HomeGroupCardModel => {
        const people = activePeopleFor(group.people ?? []);
        const transactions = (group.transactions ?? []) as GroupTransaction[];
        const participantId = resolveCurrentParticipantId(profile.username, profile.userId, people);
        const normalizedTransactions = normalizeTransactionParticipants(transactions, people);
        const byCurrency = calcUserBalanceByCurrency(normalizedTransactions, participantId);
        const currencyKeys = Object.keys(byCurrency);
        const displayBalance = currencyKeys.length <= 1
            ? convertBalances(byCurrency)
            : convertDatedTotal(calcUserDatedBalanceContributions(normalizedTransactions, participantId));
        const statusTone = getHomeGroupStatusTone(displayBalance);

        return {
            balance: displayBalance,
            balanceLabel: formatCurrencyAmountOrMasked(displayBalance, userSettings.displayCurrency, balancesVisible, locale),
            memberCount: people.length,
            memberPreview: memberPreviewFor(people),
            lastActivity: lastActivityLabel(transactions, locale, t("homePage.noActivity")),
            statusTone,
            statusLabel: statusTone === "positive"
                ? t("balances.youAreOwed")
                : statusTone === "negative"
                    ? t("balances.youOwe")
                    : t("balances.allSettled"),
        };
    }, [balancesVisible, convertBalances, convertDatedTotal, locale, profile.userId, profile.username, t, userSettings.displayCurrency]);

    const showCreateAction = !isLoading && !errorMessage;

    return {
        t,
        getPathWithLanguage,
        profile,
        locale,
        groups,
        filteredGroups,
        isLoading,
        errorMessage,
        search: {
            searchOpen,
            setSearchOpen,
            searchQuery,
            setSearchQuery,
        },
        notifications: {
            groupNotifications,
            dismissNotification,
        },
        buildCardModel,
        loadGroups,
        showCreateAction,
    };
}
